import Image from 'next/image'
import { useState, type FC, useEffect, useRef } from 'react'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Textarea } from '@wartek-id/textarea'
import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'

import { IS_DEVELOPMENT } from 'app/KinerjaShared/constants/env'
import { useIsPrior2025 } from 'app/KinerjaShared/hooks/useIsPrior2025'

interface DialogDeliveryConfirmationProps
  extends Omit<DialogProps, 'children'> {
  isFinalizing?: boolean
  onClickConfirm: () => void
  onClickCancel: () => void
}

const DialogDeliveryConfirmation: FC<DialogDeliveryConfirmationProps> = ({
  isFinalizing,
  onClickConfirm = () => {},
  onClickCancel = () => {},
  ...props
}) => {
  const isPriorTo2025 = useIsPrior2025()

  const inputRef = useRef(null)
  const [textConsent, setTextConsent] = useState('')
  const consentScript = isPriorTo2025
    ? 'Saya setuju untuk mengirim Predikat Kinerja ke Pegawai.'
    : 'SAYA SETUJU UNTUK MENGIRIM KE KEPALA SEKOLAH'
  const submitConsent = textConsent === consentScript
  const isConsentDirty = textConsent !== ''
  const isConsentFieldInvalid = !submitConsent && isConsentDirty

  // To be executed everytime the dialog is opened/reopened
  useEffect(() => {
    if (props.isOpen) {
      setTextConsent('')
      inputRef.current?.focus()
    }
  }, [props.isOpen])

  return (
    <Dialog {...props}>
      <DialogOverlay />
      <DialogContent
        className={clsx('!rounded-lg', { wiggle: !IS_DEVELOPMENT })}
      >
        <div className="p-6 space-y-4">
          <Image
            alt=""
            src="/illustrations/img-publish-data.png"
            width={84}
            height={84}
          />
          <Text variant="display-sm">
            {isPriorTo2025
              ? 'Lanjut kirim Predikat Kinerja ke Pegawai?'
              : 'Konfirmasi untuk Kirim Predikat Kinerja'}
          </Text>

          <Text variant="body-lg" color="subdued">
            {isPriorTo2025 ? (
              'Setelah dikirim, Predikat Kinerja tidak dapat diubah lagi dan Pegawai akan menerima Dokumen Evaluasi Kinerja.'
            ) : (
              <>
                Data tidak dapat diubah lagi. Kepala Sekolah akan menerima Hasil
                Penilaian Kinerja.
              </>
            )}
          </Text>

          <Text variant="body-lg" color="subdued">
            Mohon ketik kalimat ini pada kolom kalimat persetujuan:
          </Text>

          <div className="bg-blue-5 p-1 rounded">
            <Text
              variant="heading-sm"
              color="informational"
              className={clsx('select-none', '!text-blue-60')}
            >
              {consentScript}
            </Text>
          </div>

          <div className="!mt-5 w-80">
            <Text className="mb-1">Kalimat persetujuan</Text>
            <Textarea
              rows={3}
              placeholder="Ketik di sini."
              id="persetujuan"
              ref={inputRef}
              onChange={(event) => {
                setTextConsent(event.target.value)
              }}
              isInvalid={isConsentFieldInvalid}
              errorMessage={
                isConsentFieldInvalid && 'Mohon cek ejaan dan coba lagi.'
              }
              defaultValue={IS_DEVELOPMENT ? consentScript : ''}
            />
          </div>

          <div className="flex gap-5 !mt-6">
            <Button
              size="lg"
              color="white"
              disabled={isFinalizing}
              isLoading={isFinalizing}
              onClick={onClickCancel}
              data-testid="btn-cancel"
            >
              Kembali
            </Button>
            <Button
              size="lg"
              disabled={isFinalizing || !submitConsent}
              isLoading={isFinalizing}
              onClick={onClickConfirm}
              data-testid="btn-confirm"
            >
              {isPriorTo2025 ? 'Lanjut' : 'Konfirmasi'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default DialogDeliveryConfirmation
